import { getStoreData, Stores, updateData } from "services/indexedDB"
import { clearToastProgress, showToastProgress } from "./toastProgress"
import {
  DataRequestDownloadOneFile,
  handleDownloadEncrypted,
  handleDownloadOneFile,
  handleUploadFile,
} from "./handleDownloadFile"
import { ACTION_DOWNLOAD_WORKER } from "./download-worker"
import { includes, isUndefined, omit, random } from "lodash"
import Axios from "axios"
import { ACTION_WORKER } from "workers/type"
import { configureStore } from "stores/configureStore"
import { finishUpload, startUpload } from "reducers/upload-file"
import { EncryptionKeys } from "controllers/EncryptionController"
import { delay, getParameterByName, getPrivateKeyByUserId } from "helpers/utils"
import * as Sentry from "@sentry/react"
import { API_URL } from "config/environments"
import { encryptionGCMHelper } from "helpers/encryption-gcm"
import { getAccessTokenInLocalStorage } from "helpers/access_token"

export enum UploadFileType {
  GERBER = "GERBER",
  PCB_BOM = "PCB_BOM",
  PCB_STACKUP = "PCB_STACKUP",
  PCB_ASSEMBLY = "PCB_ASSEMBLY",
  PCB_ATTACHMENT = "PCB_ATTACHMENT",
  MECHANICAL_3D = "MECHANICAL_3D",
  MECHANICAL_BOM = "MECHANICAL_BOM",
  MECHANICAL_ATTACHMENT = "MECHANICAL_ATTACHMENT",
  BOM = "BOM",
  OTHER_ATTACHMENT = "OTHER_ATTACHMENT",
}

export interface DownloadQueueItem {
  apiURL: string
  name: string
  prefixStart?: string
  subName: string
  downloadId: string
  buildId?: string
  done?: boolean
  errorMessage?: string
  isFileOne?: boolean
  dataFileOne?: DataRequestDownloadOneFile
  dataBlob?: Blob
  fileHashKeyFromServer?: {
    hash_key: string
    public_key: string
  }
  multipleFile?: any
  fileFolderName?: string
  processMessage?: string
  dataUploading?: any
  dbKey?: string
}

class DownloadQueueManager {
  private queue: DownloadQueueItem[] = []
  private readonly tabId = getTabId()
  private readonly storageKey = `downloadQueue-${this.tabId}`
  private workerDownload: Worker | null = null
  private workerUpload: Worker | null = null

  constructor() {
    // this.restoreQueue()
    this.actionItem = this.actionItem.bind(this)
    this.setupBeforeUnloadWarning()
  }

  async add(item: DownloadQueueItem) {
    if (!isUndefined(item.dataUploading)) {
      await this.initWorkerUpload()
      if (
        includes(
          [
            UploadFileType.OTHER_ATTACHMENT,
            UploadFileType.MECHANICAL_ATTACHMENT,
            UploadFileType.PCB_ATTACHMENT,
          ],
          item.dataUploading.type
        ) &&
        !isUndefined(item.dataUploading.fileList) &&
        item.dataUploading.fileList.length
      ) {
        for (const file of item.dataUploading.fileList) {
          const dataItem = {
            ...item,
            name: file.name,
            downloadId: `${item.downloadId}-${file.name}`,
            dataUploading: {
              ...omit(item.dataUploading, ["fileList"]),
              file: file,
            },
            processMessage: "The file is being loaded and decrypted...",
          }
          configureStore.dispatch(startUpload(dataItem.downloadId))
          this.queue.push(dataItem)
          showToastProgress(
            this.storageKey,
            this.queue,
            this.actionItem,
            random(0, 15),
            dataItem.downloadId
          )
        }
        const fileListSorted = item.dataUploading.fileList.sort(
          (a, b) => a.size - b.size
        )

        for (const fileSort of fileListSorted) {
          const dataItem = this.queue.find(
            (i) => i.downloadId === `${item.downloadId}-${fileSort.name}`
          )
          if (dataItem) {
            await this.startWorkerUploadJob(dataItem)
            await delay(4000) // Delay to avoid overloading the worker
          }
        }
      } else {
        this.queue.push({
          ...item,
          processMessage: "The file is being loaded and decrypted...",
        })
        await this.startWorkerUploadJob(item)
        showToastProgress(
          this.storageKey,
          this.queue,
          this.actionItem,
          random(0, 15),
          item.downloadId
        )
      }
    } else {
      this.queue.push({
        ...item,
        processMessage: "Download file and signature from server...",
      })
      showToastProgress(
        this.storageKey,
        this.queue,
        this.actionItem,
        random(0, 15),
        item.downloadId
      )
      await this.initWorkerDownload()
      await this.startWorkerJob(item)
    }
    // await this.persistQueue()
  }

  private async initWorkerDownload() {
    if (this.workerDownload) return

    this.workerDownload = new Worker(
      new URL("./download-worker.ts", import.meta.url),
      {
        type: "module",
      }
    )
    console.log("init worker download")
    this.workerDownload.onmessage = async (e) =>
      this.handleWorkerDownloadMessage(e)
  }

  private async initWorkerUpload() {
    if (this.workerUpload) return

    this.workerUpload = new Worker(
      new URL("../../workers/uploadFileWorker.ts", import.meta.url),
      {
        type: "module",
      }
    )
    console.log("init worker upload")
    this.workerUpload.onmessage = async (e) => this.handleWorkerUploadMessage(e)
  }

  private async startWorkerJob(item: DownloadQueueItem) {
    const conversationEncryptionKeys = JSON.parse(
      localStorage.getItem(EncryptionKeys.conversationEncryptionKeys) || "{}"
    )
    const projectEncryptionKeys = JSON.parse(
      localStorage.getItem(EncryptionKeys.projectEncryptionKeys) || "{}"
    )
    const buildEncryptionKeys = JSON.parse(
      localStorage.getItem(EncryptionKeys.buildAdditionalInfoEncryptionKeys) ||
        "{}"
    )

    const tokenAuth = getAccessTokenInLocalStorage()
    if (item.isFileOne) {
      this.workerDownload?.postMessage({
        action: ACTION_DOWNLOAD_WORKER.START,
        payload: {
          itemQueue: item,
          tokenAuth,
        },
      })
    } else {
      const projectId = getParameterByName("project_id", item.apiURL)
      const conversationId = getParameterByName("conversation_id", item.apiURL)

      const privateKey: any = await getPrivateKeyByUserId()

      const response = await fetch(`${API_URL}${item.apiURL}`, {
        headers: {
          Authorization: tokenAuth,
          ...(item.dbKey ? { "x-db-key": item.dbKey } : {}),
        },
      })
      const responseJson = await response.json()
      const listFile = responseJson.data || []
      const fileFolderName = responseJson.name ? responseJson.name : ""
      const results = await Promise.all(
        listFile.map(async (file: any) => {
          const isAdditionalInfo =
            file.keyToFolder === "additional_info" && item.buildId
          let key = projectId ? projectEncryptionKeys[projectId] : ""
          let newMessages: any = null
          if (file.buffer) {
            if (file.conversationId) {
              key = conversationEncryptionKeys[file.conversationId]
              if (!isUndefined(file.messages) && file.messages.length) {
                newMessages = await Promise.all(
                  file.messages.map(async (el) => {
                    const messageKey =
                      el.replyKeyConversation ||
                      conversationEncryptionKeys[file.conversationId]
                    return {
                      message: el.message,
                      messageKey,
                      uniqueKey: el.uniqueKey,
                    }
                  })
                )
              }
            }
            if (isAdditionalInfo && item.buildId) {
              key = buildEncryptionKeys[item.buildId]
            }
          } else if (file.file && !file.buffer && file.buffer !== "") {
            if (file.shareKey) {
              let newKey = file.shareKey
              if (projectId) {
                const projectKey = projectEncryptionKeys[projectId]
                if (projectKey) {
                  newKey = await encryptionGCMHelper.decryptContext(
                    projectKey,
                    file.shareKey
                  )
                }
              }

              if (newKey !== file.shareKey) {
                key = newKey
              } else {
                try {
                  key = privateKey.decrypt(file.shareKey, "RSA-OAEP")
                } catch (error) {
                  console.log("Error decrypt share key")
                }
              }
            } else {
              if (conversationId && file.keyToFolder === "conversation") {
                key =
                  file.replyKeyConversation ||
                  conversationEncryptionKeys[conversationId]
              }
              if (isAdditionalInfo && item.buildId) {
                key = buildEncryptionKeys[item.buildId]
              }
            }
          }
          return {
            ...file,
            messages: newMessages,
            key,
          }
        })
      )
      this.workerDownload?.postMessage({
        action: ACTION_DOWNLOAD_WORKER.START,
        payload: {
          itemQueue: item,
          tokenAuth,
          multipleFiles: results.filter((el: any) => el?.key),
          fileFolderName,
        },
      })
    }
  }

  private async startWorkerUploadJob(item: DownloadQueueItem) {
    if (!this.workerUpload || isUndefined(item.dataUploading)) return
    const projectKeys = JSON.parse(
      localStorage.getItem(EncryptionKeys.projectEncryptionKeys) || "{}"
    )
    const projectKey = projectKeys[item.dataUploading.projectId]
    console.log("start worker upload", projectKey)
    if (
      item.dataUploading.type === UploadFileType.GERBER &&
      item.dataUploading.file
    ) {
      this.uploadFileGerberWorker(
        item.dataUploading,
        item.downloadId,
        projectKey,
        item.dataUploading.idComponentHistory || "",
        item.dataUploading.componentType
      )
    }
    if (
      includes(
        [
          UploadFileType.PCB_BOM,
          UploadFileType.PCB_STACKUP,
          UploadFileType.PCB_ASSEMBLY,
          UploadFileType.PCB_ATTACHMENT,
          UploadFileType.BOM,
          UploadFileType.MECHANICAL_3D,
          UploadFileType.MECHANICAL_BOM,
          UploadFileType.MECHANICAL_ATTACHMENT,
          UploadFileType.OTHER_ATTACHMENT,
        ],
        item.dataUploading.type
      ) &&
      item.dataUploading.file
    ) {
      this.uploadFileBaseWorker(
        item.dataUploading,
        item.downloadId,
        projectKey,
        item.dataUploading.idComponentHistory || "",
        item.dataUploading.componentType
      )
    }
  }

  private async handleWorkerDownloadMessage(e: MessageEvent) {
    if (e.data.action === ACTION_DOWNLOAD_WORKER.FINISH) {
      const { dataBlob, messageError, downloadId, fileFolderName } =
        e.data.payload
      const item = this.queue.find((i) => i.downloadId === downloadId)
      if (!item) return

      if (item.isFileOne && item.dataFileOne) {
        await this.handleSingleFile(item, dataBlob, messageError)
      } else {
        await this.handleMultipleFile(
          item,
          item.prefixStart
            ? `${item.prefixStart} ${fileFolderName}`
            : fileFolderName,
          dataBlob,
          messageError
        )
      }
      showToastProgress(this.storageKey, this.queue, this.actionItem)
      this.queue = this.queue.filter((i) => !i.done)
      showToastProgress(this.storageKey, this.queue, this.actionItem)
      // await this.persistQueue()
      if (this.queue.length === 0) {
        clearToastProgress(this.storageKey)
      }
    }
    if (e.data.action === ACTION_DOWNLOAD_WORKER.PROCESS) {
      const { message, downloadId, process } = e.data.payload
      const item = this.queue.find((i) => i.downloadId === downloadId)
      if (item) {
        item.processMessage = message
        item.done = false
        showToastProgress(
          this.storageKey,
          this.queue,
          this.actionItem,
          process,
          downloadId
        )
      }
    }
  }

  private async handleSingleFile(
    item: DownloadQueueItem,
    dataBlob: Blob | null,
    messageError: string
  ) {
    if (!dataBlob) {
      item.done = false
      item.errorMessage = messageError
      Sentry.captureException(new Error(messageError), {
        level: "error",
        extra: {
          data: item,
        },
      })
      return
    }

    item.done = false
    item.errorMessage = ""

    try {
      await handleDownloadOneFile(
        item.dataFileOne!,
        this.storageKey,
        this.queue,
        item.downloadId,
        this.actionItem,
        dataBlob
      )
      item.done = true
    } catch (error: any) {
      item.errorMessage = error?.message || "Unknown error"
      item.done = false
    }
  }

  private async handleMultipleFile(
    item: DownloadQueueItem,
    fileFolderName: string,
    dataBlob: Blob | null,
    messageError: string
  ) {
    if (!dataBlob) {
      item.done = false
      item.errorMessage = messageError
      Sentry.captureException(new Error(messageError), {
        level: "error",
        extra: {
          data: item,
        },
      })
      return
    }

    try {
      await handleDownloadEncrypted(
        item.downloadId,
        this.queue,
        this.storageKey,
        dataBlob,
        this.actionItem,
        fileFolderName
      )
      item.done = true
    } catch (error: any) {
      item.errorMessage = error?.message || "Unknown error"
      item.done = false
    }
  }

  private async persistQueue() {
    await updateData(Stores.DownloadFile, {
      id: this.storageKey,
      data: this.queue,
    })
  }

  async getQueues() {
    const stored = (await getStoreData(Stores.DownloadFile)) as {
      id: string
      data: DownloadQueueItem[]
    }[]
    return Array.isArray(stored)
      ? stored.find((item) => item.id === this.storageKey)?.data || []
      : []
  }

  async restoreQueue() {
    // const originalQueue = await this.getQueues()
    // if (originalQueue.length === 0) return
    // await this.initWorkerDownload()
    // this.queue = originalQueue.filter((item) => !item.errorMessage)
    // showToastProgress(this.storageKey, this.queue, this.actionItem)
    // for (const item of originalQueue) {
    //   if (item.done || item.errorMessage) continue
    //   await this.startWorkerJob(item)
    // }
  }
  private setupBeforeUnloadWarning() {
    window.addEventListener("beforeunload", (e) => {
      const hasUploading = this.queue.length
      if (hasUploading) {
        const message =
          "Files are being in process. Are you sure you want to leave?"
        e.preventDefault()
        e.returnValue = message // for legacy browsers
        return message
      }
    })
  }

  async checkQueueExist(downloadId: string) {
    const originalQueue = await this.getQueues()
    return originalQueue.some((item) => item.downloadId === downloadId)
  }

  private async actionItem(type: "delete", downloadId: string) {
    this.queue = this.queue.filter((q) => q.downloadId !== downloadId)
    configureStore.dispatch(finishUpload(downloadId))
    showToastProgress(this.storageKey, this.queue, this.actionItem)
    if (this.queue.length === 0) {
      clearToastProgress(this.storageKey)
    }
  }

  private async handleWorkerUploadMessage(e: MessageEvent) {
    if (e.data.action !== ACTION_WORKER.FINISH) return
    if (
      JSON.stringify(e.data.payload.fileErrors) ===
      JSON.stringify(["Private key not found"])
    ) {
      localStorage.clear()
      window.location.href = "/login"
      return
    }
    const item = this.queue.find(
      (i) => i.downloadId === e.data.payload?.downloadId && isUndefined(i.done)
    )
    if (item) {
      const result = await handleUploadFile(
        e.data.payload,
        item,
        this.queue,
        this.storageKey,
        this.actionItem
      )
      if (result === "success") {
        item.done = true
        item.errorMessage = ""
      } else {
        item.done = false
        item.errorMessage = result
      }
      configureStore.dispatch(finishUpload(e.data.payload?.downloadId))
      showToastProgress(this.storageKey, this.queue, this.actionItem)
      this.queue = this.queue.filter((i) => !i.done)
      showToastProgress(this.storageKey, this.queue, this.actionItem)
      // await this.persistQueue()
      if (this.queue.length === 0) {
        clearToastProgress(this.storageKey)
      }
    }
  }

  private async uploadFileGerberWorker(
    dataRequest: any,
    downloadId: string,
    projectKey: string,
    idComponentHistory: string,
    componentType: number
  ) {
    const request = new FormData()
    request.append("file", dataRequest.file)
    try {
      const responseSvg = await Axios.post(`/api/pcb/to-svg?loading=0`, request)

      this.workerUpload?.postMessage({
        action: ACTION_WORKER.START,
        payload: {
          isRawFile: true,
          files: [dataRequest.file],
          dataSvgs: responseSvg.data.data,
          downloadId,
          encryptionKey: projectKey,
          token: getAccessTokenInLocalStorage(),
          componentHistoryId: idComponentHistory,
          componentType,
          userId: localStorage.getItem("user_id") || "",
        },
      })
    } catch (error) {
      const item = this.queue.find((i) => i.downloadId === downloadId)
      if (item) {
        item.done = false
        item.errorMessage = "Check pcb svg file error"
        // await this.persistQueue()
        showToastProgress(this.storageKey, this.queue, this.actionItem)
      }
    }
  }
  private async uploadFileBaseWorker(
    dataRequest: any,
    downloadId: string,
    projectKey: string,
    idComponentHistory: string,
    componentType: number
  ) {
    this.workerUpload?.postMessage({
      action: ACTION_WORKER.START,
      payload: {
        files: [dataRequest.file],
        isRawFile: true,
        downloadId,
        encryptionKey: projectKey,
        token: getAccessTokenInLocalStorage(),
        componentHistoryId: idComponentHistory,
        componentType,
        userId: localStorage.getItem("user_id") || "",
      },
    })
  }
}

export const downloadQueueManager = new DownloadQueueManager()
export function getTabId() {
  if (!window.name) {
    window.name = `tab-${Date.now()}-${Math.random().toString(36).substring(2)}`
  }
  return window.name
}
